/**
 * B 表 · 对话主干 —— run / stream / cancel。
 *
 * 本文件除了三个端点封装，还有一个 {@link createAgentRun}：它只做一件事 ——
 * **把「什么时候算停了」这条契约规则实现在一处**。
 *
 * ⚠⚠ `cancel` 的 `accepted=true` **不等于已经停了**：它发送的是取消**意图**，
 *   真正的中断由 Python 在下一个 `await` 点执行。界面必须**等流上的 `done`**
 *   才切「已停止」—— 按 accepted 就切，会看到「已停止」之后气泡还在长几十毫秒。
 *   这条规则写在这里，M2 的 Zustand store 只镜像它，不重新实现一遍。
 */
import {
  cancelResponseSchema,
  runResponseSchema,
  type AgentEvent,
  type CancelRequest,
  type CancelResponse,
  type RunRequest,
  type RunResponse,
  type SseDecodeFailure,
} from '@cione/contracts'
import type { ApiError } from './errors'
import type { HttpClient } from './http'
import { readAgentStream, type StreamEndReason } from './sse'

export interface AgentApi {
  run(request: RunRequest): Promise<RunResponse>
  cancel(request: CancelRequest): Promise<CancelResponse>
}

export function createAgentApi(http: HttpClient): AgentApi {
  return {
    run(request) {
      return http.request({
        method: 'POST',
        path: '/api/agent/run',
        body: request,
        schema: runResponseSchema,
      })
    },

    cancel(request) {
      return http.request({
        method: 'POST',
        path: '/api/agent/cancel',
        body: request,
        schema: cancelResponseSchema,
      })
    },
  }
}

/** 一轮对话的阶段。 */
export type RunPhase =
  | 'idle'
  /** run 已受理，流在跑 */
  | 'running'
  /** 已发出取消意图，但**还没收到 done** —— 界面显示「停止中」 */
  | 'cancelling'
  /** 收到 done，正常收尾 */
  | 'stopped'
  /** 收到 agent_error，随后 done */
  | 'failed'
  /** 连接断了却没收到 done —— 网络断，不是正常结束 */
  | 'interrupted'

export interface AgentRunHandlers {
  onEvent?: ((event: AgentEvent) => void) | undefined
  onPhaseChange?: ((phase: RunPhase) => void) | undefined
  onDecodeFailure?: ((failure: SseDecodeFailure) => void) | undefined
  /**
   * 服务端已**受理**这一轮（B1 回了 `accepted=true`），流还没开始读。
   *
   * ⚠ 为什么要单独给这一下：界面上有些动作只能在「确定发出去了」之后做，
   *   而 `start()` 要到整条流读完才 resolve —— 拿它当判据就晚了几秒到几十秒。
   *   典型是**清空输入框**：清早了（点发送就清），未登录/断网时用户刚写的那句话就没了；
   *   清晚了（等流读完），AI 已经在回答而输入框里还留着上一句。
   *
   * ⚠ 阶段机仍然只在本文件一处：这个回调不做任何状态判断，只报告一个时点。
   */
  onAccepted?: ((response: RunResponse) => void) | undefined
}

export interface AgentRunDeps extends AgentRunHandlers {
  http: HttpClient
  agent: AgentApi
  onUnauthorized?:
    ((error: ApiError, accessTokenUsed: string | null) => Promise<boolean>) | undefined
}

export interface AgentRun {
  readonly phase: RunPhase
  readonly streamChannel: string | null
  readonly error: { code: string; message: string | null } | null
  /** 发一轮：run → 打开流 → 读到结束。 */
  start(request: RunRequest): Promise<StreamEndReason>
  /** 请求取消。⚠ 只把阶段切到 `cancelling`，切 `stopped` 要等流上的 done。 */
  cancel(): Promise<CancelResponse | null>
  /** 本地断开(离开页面等)。不通知服务端。 */
  abort(): void
}

export function createAgentRun(deps: AgentRunDeps): AgentRun {
  let phase: RunPhase = 'idle'
  let streamChannel: string | null = null
  let error: { code: string; message: string | null } | null = null
  let controller: AbortController | null = null

  function setPhase(next: RunPhase): void {
    if (phase === next) return
    phase = next
    deps.onPhaseChange?.(next)
  }

  return {
    get phase() {
      return phase
    },
    get streamChannel() {
      return streamChannel
    },
    get error() {
      return error
    },

    async start(request) {
      error = null
      controller = new AbortController()
      setPhase('running')

      const accepted = await deps.agent.run(request)
      streamChannel = accepted.streamChannel
      // ⚠ 在读流【之前】报告受理：读流会一直阻塞到 done，放在后面就没有意义了
      deps.onAccepted?.(accepted)

      const reason = await readAgentStream({
        http: deps.http,
        streamChannel: accepted.streamChannel,
        signal: controller.signal,
        ...(deps.onUnauthorized ? { onUnauthorized: deps.onUnauthorized } : {}),
        ...(deps.onDecodeFailure ? { onDecodeFailure: deps.onDecodeFailure } : {}),
        onEvent(event) {
          // ⚠ agent_error 必在 done 之前(契约 §5.2 保证 1)，所以这里只记不切阶段
          if (event.kind === 'error') error = { code: event.code, message: event.message }
          deps.onEvent?.(event)
        },
      })

      if (reason === 'done') setPhase(error ? 'failed' : 'stopped')
      else if (reason === 'aborted') setPhase('stopped')
      else setPhase('interrupted')

      return reason
    },

    async cancel() {
      if (phase !== 'running') return null
      // ⚠⚠ 只切到「停止中」。切 stopped 必须等流上的 done —— 见文件头注。
      setPhase('cancelling')
      if (!streamChannel) return null
      return deps.agent.cancel({ streamChannel })
    },

    abort() {
      controller?.abort()
    },
  }
}
